import React from 'react'
import { ButtonStyle, ButtonShape, Button } from '../../../components/simple/button'
import { Guardian, Student, User } from '../../../providers/context'
import { IconAnnouncementCall } from '../../../icons/announcementCall'
import { IconEmail, IconText, IconPhone } from '../../../icons/library'
import { Checkbox, LabeledCheckbox } from '../../../components/form/checkbox'
import { SendType } from './composeTypes'
import { useTranslation } from '../../../providers/translation'

export const TeacherTypeSelector: React.FC<{
  sendTypes: Set<SendType>
  setSendTypes: (types: Set<SendType>) => void
  massAllowReplying: boolean
  setMassAllowReplying: (allow: boolean) => void
  user: User
}> = ({ sendTypes, setSendTypes, user, massAllowReplying, setMassAllowReplying }) => {
  const phoneOptionAvailable = user.district.phoneEnabled
  const toggleSendType = (type: SendType) => {
    let newTypes = new Set(sendTypes)
    if (sendTypes.has(type)) {
      if (type === 'mass') {
        newTypes.delete('mass')
        if (newTypes.size > 1 && newTypes.has('phone')) newTypes.delete('phone')
      } else if ([...newTypes].filter((t) => t !== 'mass').length === 1) {
        if (type === 'email' && phoneOptionAvailable) {
          newTypes.add('text')
          newTypes.delete('email')
        }
        if (type === 'text') {
          newTypes.delete('text')
          newTypes.add('email')
        }
      } else {
        newTypes.delete(type)
      }
    } else {
      newTypes.add(type)
      if (type === 'phone' && !newTypes.has('mass')) newTypes = new Set(['phone'])
      if (type !== 'phone' && !newTypes.has('mass') && newTypes.has('phone')) newTypes.delete('phone')
    }
    setSendTypes(newTypes)
  }

  React.useEffect(() => {
    if (!phoneOptionAvailable && (sendTypes.has('phone') || sendTypes.has('text')))
      setSendTypes(new Set([...sendTypes].filter((t) => t !== 'phone' && t !== 'text')))
    if (!user.massCallingEnabled && sendTypes.has('mass'))
      setSendTypes(new Set([...sendTypes].filter((t) => t !== 'mass')))
  }, [user, sendTypes])

  return (
    <div className="flex items-center overflow-x-auto" id="tutorial_typeSelector">
      <Button
        buttonStyle={sendTypes.has('email') ? ButtonStyle.logo : ButtonStyle.light}
        shape={phoneOptionAvailable || user.massCallingEnabled ? ButtonShape.groupStart : ButtonShape.standard}
        onClick={() => toggleSendType('email')}
        className={`-sm:px-7 ${sendTypes.has('mass') ? 'border-b-[3px] border-l-[3px] border-t-[3px] border-charcoal' : ''}`}
        tooltip={
          sendTypes.has('mass') ? (
            'Send mass emails'
          ) : (
            <div className="text-nowrap">
              Send as email
              <br />
              {user.district.phoneEnabled && <i className="text-sm">Recipients without email will get a text</i>}
            </div>
          )
        }
      >
        <IconEmail />
        <span className="-sm:hidden">Email</span>
      </Button>
      {phoneOptionAvailable && (
        <Button
          buttonStyle={sendTypes.has('text') ? ButtonStyle.logo : ButtonStyle.light}
          shape={ButtonShape.groupMiddle}
          onClick={() => toggleSendType('text')}
          className={`-sm:px-7 ${sendTypes.has('mass') ? 'border-b-[3px] border-t-[3px] border-charcoal' : ''}`}
          id="tutorial_typeSelectorText"
          tooltip={
            sendTypes.has('mass') ? (
              'Send mass texts'
            ) : (
              <div className="text-nowrap">
                Send as text
                <br />
                <i className="text-sm">Recipients without text will get an email</i>
              </div>
            )
          }
        >
          <IconText />
          <span className="-sm:hidden">Text</span>
        </Button>
      )}
      {phoneOptionAvailable && (
        <Button
          buttonStyle={sendTypes.has('phone') ? ButtonStyle.logo : ButtonStyle.light}
          shape={user.massCallingEnabled ? ButtonShape.groupMiddle : ButtonShape.groupEnd}
          onClick={() => toggleSendType('phone')}
          className={`-sm:px-7 ${sendTypes.has('mass') ? 'border-b-[3px] border-t-[3px] border-charcoal' : ''}`}
          tooltip={sendTypes.has('mass') ? 'Send mass calls' : 'Make a call'}
        >
          <IconPhone />
          <span className="-sm:hidden">Call</span>
        </Button>
      )}
      {user.massCallingEnabled && (
        <Button
          buttonStyle={sendTypes.has('mass') ? ButtonStyle.charcoal : ButtonStyle.light}
          shape={ButtonShape.groupEnd}
          onClick={() => toggleSendType('mass')}
          className={`-sm:px-7 ${sendTypes.has('mass') ? 'border-[3px] border-charcoal' : ''}`}
        >
          <IconAnnouncementCall />
          <span className="-sm:hidden">Broadcast</span>
        </Button>
      )}
      {sendTypes.has('mass') && (
        <LabeledCheckbox
          checked={massAllowReplying}
          onChecked={setMassAllowReplying}
          label="Allow Replying"
          labelBefore
          className="ml-3"
        />
      )}
    </div>
  )
}

export const FamilyTypeSelector: React.FC<{
  sendTypes: Set<SendType>
  setSendTypes: React.Dispatch<React.SetStateAction<Set<SendType>>>
  user: Student | Guardian
}> = ({ sendTypes, setSendTypes, user }) => {
  const { tr } = useTranslation()

  const canChooseText = !!user.phoneNumber?.trim() && user.receiveTexts && user.district.phoneEnabled
  const canChooseEmail = (user.email.trim() !== '' && (user.type === 'student' || user.receiveEmails)) || !canChooseText

  const toggleSendType = (type: SendType) => {
    const newTypes = new Set(sendTypes)
    if (sendTypes.has(type) && !(type === 'email' ? canChooseText : canChooseEmail)) return
    if (sendTypes.has(type)) newTypes.delete(type)
    else newTypes.add(type)
    if (newTypes.size === 0) newTypes.add(type === 'email' ? 'text' : 'email')
    setSendTypes(newTypes)
  }

  React.useEffect(() => {
    if (sendTypes.size === 0) setSendTypes(new Set([canChooseEmail ? 'email' : 'text']))
    if (sendTypes.has('text') && !canChooseText) setSendTypes((s) => new Set([...s].filter((t) => t !== 'text')))
    if (sendTypes.has('email') && !canChooseEmail) setSendTypes((s) => new Set([...s].filter((t) => t !== 'email')))
  }, [sendTypes, user])

  return (
    <div className="flex items-center">
      <span className="mr-3">{tr('Reply to me through:')}</span>
      {canChooseEmail && (
        <>
          <Checkbox
            charcoal
            checked={sendTypes.has('email')}
            onChecked={() => toggleSendType('email')}
            className="mr-1"
          />
          <span className="mr-3">{tr('Email')}</span>
        </>
      )}
      {canChooseText && (
        <>
          <Checkbox
            charcoal
            checked={sendTypes.has('text')}
            onChecked={() => toggleSendType('text')}
            className="mr-1"
          />
          <span className="">{tr('Text')}</span>
        </>
      )}
    </div>
  )
}
